import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function useDetectedAnomalies(billId?: string, workspaceId?: string) {
  return useQuery({
    queryKey: ['detected-anomalies', billId, workspaceId],
    queryFn: async () => {
      let query = supabase
        .from('detected_anomalies')
        .select('*')
        .eq('is_resolved', false);

      if (billId) {
        query = query.eq('bill_id', billId);
      }

      const { data, error } = await query.order('detected_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });
}

export function useHistoricalBaselines(customerId: string, supplierId?: string) {
  return useQuery({
    queryKey: ['historical-baselines', customerId, supplierId],
    queryFn: async () => {
      let query = supabase
        .from('bill_historical_baselines')
        .select('*')
        .eq('customer_id', customerId);

      if (supplierId) {
        query = query.eq('supplier_id', supplierId);
      }

      const { data, error } = await query.order('updated_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!customerId
  });
}

export function useRunAnomalyDetection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      billId: string;
      customerId: string;
      supplierId: string;
      commodityType: string;
      amount: number;
      usage?: number;
      billDate: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('anomaly-detection-engine', {
        body: input
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['detected-anomalies'] });
      if (data.anomaliesDetected > 0) {
        toast.warning(`${data.anomaliesDetected} anomal${data.anomaliesDetected > 1 ? 'ies' : 'y'} detected`);
      }
    },
    onError: (error: Error) => {
      toast.error('Anomaly detection failed: ' + error.message);
    }
  });
}

export function useResolveAnomaly() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ anomalyId, resolutionNotes }: { anomalyId: string; resolutionNotes: string }) => {
      const { data, error } = await supabase
        .from('detected_anomalies')
        .update({
          is_resolved: true,
          resolution_notes: resolutionNotes
        })
        .eq('id', anomalyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['detected-anomalies'] });
      toast.success('Anomaly resolved');
    },
    onError: (error: Error) => {
      toast.error('Failed to resolve anomaly: ' + error.message);
    }
  });
}
